import { extractSdkErrorDetail, getResponseStatus } from "@istari-frontend/shared";

import { BranchingError } from "~/branching/model/branching-error";

// Stopgap until the backend exposes a structured error code: some 403 responses
// carry archived-branch prose in `detail` without a 400/422 status.
function isArchivedBranchDetail(detail: string): boolean {
  return /archived/i.test(detail) && /cannot|can't|not allowed|rename|update/i.test(detail);
}

function clientErrorMessage(status: number, detail: string | undefined): string {
  if (detail) {
    return detail;
  }
  return status === 422 ? "The branch name could not be updated. Check the name and try again." : "Invalid request";
}

/**
 * Map SDK/HTTP errors to a typed `BranchingError` for the domain port.
 */
export async function toBranchingError(cause: unknown): Promise<BranchingError> {
  if (cause instanceof BranchingError) {
    return cause;
  }

  const status = getResponseStatus(cause);
  if (status !== undefined) {
    const detail = await extractSdkErrorDetail(cause, { joinValidationDetail: true }).catch(() => undefined);

    if (status === 404) {
      return new BranchingError("not-found", detail ?? "Resource not found", cause);
    }
    if (status === 0 || status >= 500) {
      return new BranchingError("unavailable", detail ?? "Service unavailable", cause);
    }
    if (status === 409) {
      return new BranchingError("conflict", detail ?? "Request conflict", cause);
    }
    if (detail && isArchivedBranchDetail(detail)) {
      return new BranchingError("invalid", detail, cause);
    }
    if (status === 400 || status === 422) {
      return new BranchingError("invalid", clientErrorMessage(status, detail), cause);
    }
  }

  if (cause instanceof Error) {
    return new BranchingError("unknown", cause.message, cause);
  }
  return new BranchingError("unknown", "Unknown error", cause);
}
